import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertCircle, Paperclip, Upload, X } from 'lucide-react'
import { queryKeys } from '@/lib/query-client'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { uploadPaymentProof } from './api'
import styles from './PaymentProofUpload.module.css'

interface PaymentProofUploadProps {
  /** The booking's id. A trip must pass `trip.requestId`. */
  tripRequestId: number
  /** Whether a proof is already on file, which makes this a replacement. */
  hasProof: boolean
  /**
   * Called once the image is stored.
   *
   * The host is rendering a captured payment summary whose `hasProof` is now
   * out of date, and so is the assignment gate that reads it. The host closes
   * rather than show an operator a booking that still says "awaiting proof"
   * after they have just attached one.
   */
  onUploaded?: () => void
}

/**
 * The formats the server accepts, decided from the file's own bytes rather than
 * its name. Repeated here so an operator is told before a 4MB upload travels,
 * not after — the server remains the one that decides.
 */
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const ACCEPTED_LABEL = 'JPEG, PNG or WebP'

/** Matches `app.upload.payment-proof.max-bytes` on the server. */
const MAX_BYTES = 4 * 1024 * 1024

/**
 * Attaching a proof of payment on the customer's behalf.
 *
 * The customer's own upload lives in the mobile app. This exists because the
 * receipt frequently does not arrive that way at all: a customer who was moved
 * onto Instapay over the phone sends their screenshot to the person they were
 * speaking to, by WhatsApp or email. Without somewhere to put it, that booking
 * can never satisfy the proof requirement standing between it and a driver.
 *
 * The chosen file is previewed before it is sent. An operator working through a
 * queue of receipts will eventually pick the wrong screenshot, and seeing it
 * costs nothing next to attaching one customer's bank details to another
 * customer's booking.
 */
export function PaymentProofUpload({ tripRequestId, hasProof, onUploaded }: PaymentProofUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [chosen, setChosen] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // A blob URL pins its data until revoked, and this component sits in a drawer
  // an operator opens once per booking all day.
  useEffect(() => {
    if (!chosen) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(chosen)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [chosen])

  // A different booking is a different receipt. Anything half-chosen belongs to
  // the booking that was on screen a moment ago, not this one.
  useEffect(() => {
    setChosen(null)
    setError(null)
    if (inputRef.current) inputRef.current.value = ''
  }, [tripRequestId])

  const clear = useCallback(() => {
    setChosen(null)
    setError(null)
    if (inputRef.current) inputRef.current.value = ''
  }, [])

  const mutation = useAdminMutation({
    mutationFn: (file: File) => uploadPaymentProof(tripRequestId, file),
    successTitle: hasProof ? 'Proof of payment replaced' : 'Proof of payment attached',
    successDescription: () =>
      'The booking is still unpaid — verify the payment once the transfer shows on the account.',
    errorTitle: 'The proof of payment was not attached',
    // hasProof and the assignment gate both move, so the queues that render
    // either one are stale: a row would otherwise still say "Awaiting proof"
    // and keep refusing the driver the operator has just unblocked.
    invalidate: [
      queryKeys.payments.all,
      queryKeys.tripRequests.all,
      queryKeys.trips.all,
      queryKeys.changeLog.all,
    ],
    onSuccess: () => {
      clear()
      onUploaded?.()
    },
  })

  const choose = (file: File | undefined) => {
    if (!file) return

    // Checked by declared type, which is a hint and not proof — the server
    // inspects the bytes. This only spares an operator a pointless upload and a
    // round trip to be told the same thing.
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setChosen(null)
      setError(`That file is not an image the system accepts. Use ${ACCEPTED_LABEL}.`)
      return
    }
    if (file.size > MAX_BYTES) {
      setChosen(null)
      setError(`That image is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_BYTES)}.`)
      return
    }

    setError(null)
    setChosen(file)
  }

  return (
    <div className={styles.wrapper}>
      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        accept={ACCEPTED_TYPES.join(',')}
        aria-label="Choose a proof of payment image"
        onChange={(event) => choose(event.target.files?.[0])}
        disabled={mutation.isPending}
      />

      {!chosen ? (
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<Paperclip size={16} aria-hidden="true" />}
          onClick={() => inputRef.current?.click()}
          disabled={mutation.isPending}
        >
          {hasProof ? 'Replace proof of payment' : 'Attach proof of payment'}
        </Button>
      ) : (
        <div className={styles.chosen}>
          {previewUrl ? (
            <img src={previewUrl} alt={`Preview of ${chosen.name}`} className={styles.preview} />
          ) : null}

          <div className={styles.chosenText}>
            <span className={styles.filename}>{chosen.name}</span>
            <span className={styles.filesize}>{formatBytes(chosen.size)}</span>
          </div>

          <div className={styles.chosenActions}>
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Upload size={16} aria-hidden="true" />}
              loading={mutation.isPending}
              onClick={() => mutation.mutate(chosen)}
            >
              {mutation.isPending ? 'Uploading…' : hasProof ? 'Replace' : 'Upload'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<X size={16} aria-hidden="true" />}
              onClick={clear}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error ? (
        <p className={styles.error} role="alert">
          <AlertCircle size={14} aria-hidden="true" />
          {error}
        </p>
      ) : null}

      {/*
        Said at the point of upload rather than only in the dialog that led here.
        An operator attaching a receipt has every reason to assume that settles
        it, and the whole design depends on their knowing it does not.
      */}
      <p className={styles.hint}>
        {ACCEPTED_LABEL}, up to {formatBytes(MAX_BYTES)}. Attaching a receipt does not mark the booking
        paid — verify the payment separately once the transfer shows on the account.
      </p>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
